"use client";

import * as React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button, buttonVariants } from "@/components/ui/button";
import { CancelButton } from '@/components/ui/cancel-button';
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check } from "lucide-react";
import { Tagline } from "@/components/pro-blocks/landing-page/tagline";
import { VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { PlanChangeModal } from "@/components/PlanChangeModal";
import { CreditsExplanationModal } from "@/components/CreditsExplanationModal";
import { VersionHistoryExplanationModal } from "@/components/VersionHistoryExplanationModal";
import { InfoHint } from "@/components/InfoHint";
import { API_BASE } from "@/config";
import { readJsonWithOnboarding } from "@/onboarding/notify-onboarding-update";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { useTierDefaults } from "@/hooks/use-tier-defaults";
import type { TierDefaults } from "@/types/tier-limits";
import { getCurrencySymbol, isEuropeanUser } from "@/utils/currency";
import { type SupportedLocale } from '../../../../locales/locale';
import {
  FREE_TIER_FEATURE_ORDER,
  getTierPlanFeatureItems,
  PRO_TIER_FEATURE_ORDER,
} from "@/utils/tier-plan-features";
import { FALLBACK_TIER_DEFAULTS } from "@/data/fallback-tier-defaults";

const getPricingData = (
  t: (key: string) => string,
  locale: SupportedLocale,
  tierDefaults: TierDefaults | null,
) => ({
  plans: [
    {
      name: t('plan_name_free'),
      tier: "free" as const,
      description: t('plan_free_description'),
      features: getTierPlanFeatureItems(
        (tierDefaults ?? FALLBACK_TIER_DEFAULTS).free,
        FREE_TIER_FEATURE_ORDER,
        t,
        locale,
      ),
      pricing: {
        monthly: 0,
        annually: 0,
      },
      variant: "secondary",
    },
    {
      name: t('plan_name_pro'),
      tier: "pro" as const,
      description: t('plan_pro_description'),
      badge: t('most_popular'),
      features: getTierPlanFeatureItems(
        (tierDefaults ?? FALLBACK_TIER_DEFAULTS).pro,
        PRO_TIER_FEATURE_ORDER,
        t,
        locale,
      ),
      pricing: {
        monthly: 15,
        annually: 144,
      },
      variant: "default",
    },
  ],
});

interface PricingSection1Props {
  currentTier?: 'free' | 'pro' | 'editorial';
  defaultBillingPeriod?: 'monthly' | 'annually';
  hideHeader?: boolean;
  cancelAtPeriodEnd?: boolean;
}

export function PricingSection1({ currentTier, defaultBillingPeriod = 'monthly', hideHeader = false, cancelAtPeriodEnd = false }: PricingSection1Props) {
  const [billingPeriod, setBillingPeriod] = React.useState(defaultBillingPeriod);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [creditsModalOpen, setCreditsModalOpen] = React.useState(false);
  const [versionHistoryModalOpen, setVersionHistoryModalOpen] = React.useState(false);
  const [selectedTier, setSelectedTier] = React.useState<'free' | 'pro' | 'editorial' | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = React.useState(false);
  const [feedbackMessage, setFeedbackMessage] = React.useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = React.useState(false);
  const { token } = useAuth();
  const { t, locale } = useLocale();
  const { tierDefaults } = useTierDefaults();
  const currencySymbol = getCurrencySymbol();
  const pricingData = React.useMemo(
    () => getPricingData(t, locale, tierDefaults),
    [t, locale, tierDefaults],
  );

  React.useEffect(() => {
    if (!isFeedbackModalOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsFeedbackModalOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFeedbackModalOpen]);

  const handleSubmitFeedback = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!feedbackMessage.trim() || isSubmittingFeedback) {
      return;
    }

    setIsSubmittingFeedback(true);

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE}/api/user-feedback`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: feedbackMessage.trim() }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      await readJsonWithOnboarding(response);
      setFeedbackMessage('');
      setIsFeedbackModalOpen(false);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert(t('failed_to_submit_feedback'));
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const getButtonText = (planTier: 'free' | 'pro' | 'editorial') => {
    if (currentTier === planTier) {
      return t('current_plan');
    }

    const tierOrder: Record<'free' | 'pro' | 'editorial', number> = { free: 0, pro: 1, editorial: 2 };
    const currentOrder = currentTier ? tierOrder[currentTier] : -1;
    const planOrder = tierOrder[planTier];

    if (planOrder > currentOrder) {
      if (planTier === "editorial") {
        return t('start_editorial_workspace');
      }
      if (planTier === "pro") {
        return t('upgrade_to_pro');
      }
    } else if (planOrder < currentOrder) {
      const plan = pricingData.plans.find(p => p.tier === planTier);
      const planName = plan?.name || (planTier === "free" ? t('plan_name_free') : planTier === "pro" ? t('plan_name_pro') : t('plan_name_editorial'));
      return t('downgrade_to_plan').replace('{plan}', planName);
    }

    return t('purchase_plan');
  };

  const handlePlanButtonClick = async (planTier: 'free' | 'pro' | 'editorial'): Promise<void> => {
    if (currentTier === planTier) {
      return;
    }

    try {
      if (token) {
        await fetch(`${API_BASE}/api/observability-events`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            eventType: 'user_clicked_plan',
            userMessage: `User clicked to change plan from ${currentTier || 'unknown'} to ${planTier}`,
          }),
        });
      }
    } catch (error) {
      console.error('Error registering plan click event:', error);
    }

    if (planTier === 'free') {
      setSelectedTier(planTier);
      setModalOpen(true);
      return;
    }

    if (!token) {
      window.location.href = '/register';
      return;
    }

    setIsLoading(true);
    try {
      const currency = isEuropeanUser() ? 'eur' : 'usd';
      const interval = billingPeriod === 'annually' ? 'yearly' : 'monthly';
      const successUrl = `${window.location.origin}/desk/usage-and-billing?upgrade=success`;
      const cancelUrl = `${window.location.origin}/desk/usage-and-billing?upgrade=canceled`;

      const response = await fetch(`${API_BASE}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          tier: planTier,
          interval,
          currency,
          successUrl,
          cancelUrl,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create checkout session');
      }

      const { checkoutUrl } = await response.json();
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert(t('error_creating_checkout'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    // Main pricing section container
    <section
      className="bg-background w-full overflow-x-hidden"
      style={{ paddingTop: '2rem', paddingBottom: '2rem', width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}
      aria-labelledby="pricing-section-title"
    >
      <div className="w-full max-w-full mx-auto px-2 sm:px-4 lg:px-6 box-border" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <div className="flex flex-col items-center gap-10 md:gap-12 w-full max-w-full box-border" style={{ width: '100%', maxWidth: '100%' }}>
          {/* Section Header */}
          {!hideHeader && (
            <div className="section-title-gap-lg flex max-w-xl flex-col items-center text-center">
              {/* Main Title */}
              <h2
                id="pricing-section-title"
                className="text-4xl md:text-5xl font-bold text-foreground mb-8"
              >
                {t('pricing_title')}
              </h2>
              {/* Section Description */}
              <p className="text-muted-foreground text-base">
                {t('pricing_subtitle')}
              </p>
              <p className="text-muted-foreground text-base">
                {t('pricing_description')}
              </p>
            </div>
          )}

          {/* Billing Period Toggle */}
          <Tabs
            value={billingPeriod}
            onValueChange={(value) => setBillingPeriod(value as 'monthly' | 'annually')}
            className="w-fit"
          >
            <TabsList className="bg-muted h-10">
              <TabsTrigger
                value="monthly"
                className="data-[state=active]:bg-background px-3 py-1.5 data-[state=active]:shadow-sm"
              >
                {t('monthly')}
              </TabsTrigger>
              <TabsTrigger
                value="annually"
                className="data-[state=active]:bg-background px-3 py-1.5 data-[state=active]:shadow-sm flex items-center gap-1.5"
              >
                {t('annually')}
                <Badge className="text-[10px] px-1.5 py-0.5 h-4">
                  {t('save_percentage')}
                </Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Pricing Cards Grid */}
          <div className="flex flex-col lg:flex-row lg:justify-center lg:items-stretch gap-2 sm:gap-3 lg:gap-4 w-full">
            {pricingData.plans.map((plan, index) => (
              <Card
                key={plan.name}
                className={`rounded-xl p-2.5 sm:p-4 lg:p-6 flex flex-col w-full lg:w-[400px] lg:flex-shrink-0 ${currentTier === plan.tier ? "border-primary border-2" : ""}`}
              >
                <CardContent className="flex flex-col gap-8 p-0">
                  {/* Plan Header */}
                  <div className="flex flex-col gap-6">
                    {/* Plan Title and Description */}
                    <div className="relative flex flex-col gap-3 min-h-[200px]">
                      {/* Current Plan, Won't Renew, or Popular Plan Badge */}
                      {currentTier === plan.tier ? (
                        cancelAtPeriodEnd ? (
                          <Badge className="absolute top-1 right-0 w-fit bg-warning hover:bg-warning text-white border-warning">
                            {t('wont_renew')}
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="absolute top-1 right-0 w-fit">
                            {t('current_plan')}
                          </Badge>
                        )
                      ) : plan.badge ? (
                        <Badge className="absolute top-1 right-0 w-fit">
                          {plan.badge}
                        </Badge>
                      ) : null}
                      <h3
                        className={`text-lg font-semibold ${currentTier === plan.tier ? "text-primary" : ""}`}
                      >
                        {plan.name}
                      </h3>
                      <p className="text-muted-foreground text-sm" dangerouslySetInnerHTML={{ __html: plan.description }} />
                    </div>

                    {/* Pricing Display */}
                    <div className="flex flex-col gap-1 min-h-[48px]">
                      <div className="flex items-end gap-0.5">
                        <span className="text-4xl font-semibold">
                          {currencySymbol}
                          {billingPeriod === "annually"
                            ? Math.round((plan.pricing.annually / 12) * 100) / 100
                            : plan.pricing.monthly}
                        </span>
                        <span className="text-muted-foreground text-base">
                          /{t('month')}
                        </span>
                      </div>
                      <span className="text-muted-foreground text-xs">
                        {plan.tier === "free" ? t('no_credit_card_required') : (billingPeriod === "annually" ? t('billed_annually') : t('billed_monthly'))}
                      </span>
                    </div>

                    {/* CTA Button, Current Plan, or Won't Renew */}
                    {currentTier === plan.tier ? (
                      <div
                        className={cn(
                          buttonVariants({
                            variant: cancelAtPeriodEnd ? "outline" : "ghost" as VariantProps<
                              typeof buttonVariants
                            >["variant"],
                          }),
                          "w-full cursor-default",
                          cancelAtPeriodEnd ? "border-warning text-warning bg-gray-100 hover:bg-gray-100" : "opacity-75"
                        )}
                      >
                        {cancelAtPeriodEnd ? t('wont_renew') : t('current_plan')}
                      </div>
                    ) : (
                      <Button
                        variant={
                          plan.variant as VariantProps<
                            typeof buttonVariants
                          >["variant"]
                        }
                        className="w-full"
                        onClick={() => handlePlanButtonClick(plan.tier)}
                        disabled={isLoading}
                      >
                        {isLoading ? t('loading') : getButtonText(plan.tier)}
                      </Button>
                    )}
                  </div>

                  {/* Features List */}
                  <div className="flex flex-col gap-4">
                    <p className="text-sm font-medium">
                      {index === 0 || index === 1
                        ? t('whats_included')
                        : t('everything_in_plan_plus').replace('{plan}', pricingData.plans[index - 1].name)}
                    </p>
                    <div className="flex flex-col gap-4">
                      {plan.features.map((feature, i) => {
                        const isCreditFeature = feature.kind === 'credits';
                        const isHistoryFeature = feature.kind === 'history';
                        return (
                          <div key={i} className="flex items-center gap-3">
                            {/* Feature Checkmark */}
                            <Check className="text-primary h-5 w-5" />
                            {/* Feature Name */}
                            <span className="text-muted-foreground flex-1 text-sm">
                              {feature.name}
                            </span>
                            {feature.tooltip && (
                              <InfoHint
                                label={`More information about ${feature.name}`}
                                align="end"
                                footerAction={
                                  isCreditFeature
                                    ? {
                                        label: t('whats_a_credit'),
                                        onClick: () => setCreditsModalOpen(true),
                                      }
                                    : isHistoryFeature
                                      ? {
                                          label: t('how_version_history_works'),
                                          onClick: () => setVersionHistoryModalOpen(true),
                                        }
                                      : undefined
                                }
                              >
                                <p>{feature.tooltip}</p>
                              </InfoHint>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Work with manuscripts section */}
          <div className="flex flex-col items-center gap-4 text-center max-w-2xl mt-8">
            <p className="text-lg font-medium">
              {t('work_with_manuscripts_title')}
            </p>
            <p className="text-muted-foreground text-sm">
              {t('work_with_manuscripts_description')}{' '}
              <button
                onClick={() => setIsFeedbackModalOpen(true)}
                className="text-primary hover:underline underline-offset-2"
              >
                {t('get_in_touch')}
              </button>
              {' or '}{t('write_to_us')}:{' '}
              <a
                href="mailto:contact@scribane.com"
                className="text-primary hover:underline underline-offset-2"
              >
                contact@scribane.com
              </a>
              .
            </p>
          </div>
        </div>
      </div>
      {selectedTier && (
        <PlanChangeModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          currentTier={currentTier}
          targetTier={selectedTier}
        />
      )}
      <CreditsExplanationModal
        open={creditsModalOpen}
        onOpenChange={setCreditsModalOpen}
      />
      <VersionHistoryExplanationModal
        open={versionHistoryModalOpen}
        onOpenChange={setVersionHistoryModalOpen}
      />
      {isFeedbackModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--scribane-black)]/50"
          onClick={() => setIsFeedbackModalOpen(false)}
        >
          <div
            className="bg-background rounded-lg p-6 max-w-md w-[90%] max-h-[80vh] overflow-auto shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold mb-2">{t('contact_scribane_team_title')}</h2>
            <p className="text-sm text-muted-foreground mb-4">{t('contact_scribane_team_description')}</p>
            <form onSubmit={handleSubmitFeedback}>
              <textarea
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                placeholder={t('feedback_placeholder') || 'Your feedback...'}
                className="w-full min-h-[100px] p-3 border rounded-md mb-4 resize-y"
                required
              />
              <div className="flex justify-end gap-2">
                <CancelButton
                  type="button"
                  onClick={() => setIsFeedbackModalOpen(false)}
                >
                  {t('cancel') || 'Cancel'}
                </CancelButton>
                <Button type="submit" disabled={isSubmittingFeedback}>
                  {isSubmittingFeedback ? (t('submitting') || 'Submitting...') : t('submit')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
